import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { MessageSquare, ShoppingCart, Target, TrendingUp, Users, Clock, CheckCircle, ArrowRight, Calendar } from "lucide-react";

const SuccessCases = () => {
  const cases = [
    {
      id: 1,
      title: "Asistente Ejecutivo Inteligente",
      subtitle: "Automatización de gestión directiva",
      company: "Empresa de Servicios Profesionales",
      sector: "Consultoría",
      challenge: "La directiva necesitaba acceso inmediato a información crítica de la empresa y gestión de tareas administrativas sin interrumpir su trabajo diario.",
      solution: "Desarrollamos un asistente de IA integrado en WhatsApp que permite a los ejecutivos realizar consultas y ejecutar acciones mediante lenguaje natural, procesando texto, audio e imágenes.",
      features: [
        "Consultas en tiempo real sobre facturación y contabilidad",
        "Acceso a estadísticas y KPIs de la empresa",
        "Agendamiento automático de reuniones",
        "Creación y gestión de recordatorios",
        "Procesamiento de documentos por imagen",
        "Respuestas por voz y texto"
      ],
      results: [
        "Reducción del 70% en tiempo de gestión administrativa",
        "Acceso 24/7 a información crítica",
        "Mejora del 85% en eficiencia ejecutiva",
        "Eliminación de consultas telefónicas internas"
      ],
      icon: <MessageSquare className="h-8 w-8 text-blue-600" />,
      color: "blue",
      metrics: {
        timeSaved: "70%",
        efficiency: "85%",
        availability: "24/7"
      }
    },
    {
      id: 2,
      title: "Recuperación Inteligente de Carritos",
      subtitle: "Automatización de ventas abandonadas",
      company: "Ecommerce de Moda",
      sector: "Retail Online",
      challenge: "La empresa perdía el 65% de sus ventas potenciales debido a carritos abandonados, sin un sistema efectivo para recuperar estos clientes.",
      solution: "Implementamos un sistema de IA que identifica carritos abandonados y contacta automáticamente a los clientes con ofertas personalizadas a través de WhatsApp y email.",
      features: [
        "Detección automática de carritos abandonados",
        "Análisis del comportamiento de compra",
        "Ofertas personalizadas basadas en historial",
        "Comunicación multicanal (WhatsApp + Email)",
        "Seguimiento de conversiones en tiempo real",
        "Integración con sistema de inventario"
      ],
      results: [
        "Recuperación del 45% de carritos abandonados",
        "Incremento del 30% en ventas totales",
        "ROI del 280% en 3 meses",
        "Reducción del 60% en costes de marketing"
      ],
      icon: <ShoppingCart className="h-8 w-8 text-green-600" />,
      color: "green",
      metrics: {
        recovery: "45%",
        salesIncrease: "30%",
        roi: "280%"
      }
    },
    {
      id: 3,
      title: "Prospección Automatizada",
      subtitle: "Generación inteligente de leads",
      company: "Agencia de Marketing Digital",
      sector: "Servicios B2B",
      challenge: "La agencia necesitaba ampliar su base de clientes pero el proceso manual de prospección era lento y poco efectivo, generando solo 2-3 leads cualificados por semana.",
      solution: "Desarrollamos un bot de prospección que identifica automáticamente potenciales clientes, analiza su perfil y los contacta con mensajes personalizados como icebreaker inicial.",
      features: [
        "Búsqueda automática de leads cualificados",
        "Análisis de perfil y necesidades del cliente",
        "Mensajes personalizados como icebreaker",
        "Seguimiento automático de respuestas",
        "Clasificación de leads por probabilidad",
        "Integración con CRM existente"
      ],
      results: [
        "Incremento del 400% en leads generados",
        "Mejora del 60% en tasa de respuesta",
        "Reducción del 80% en tiempo de prospección",
        "Aumento del 150% en clientes nuevos"
      ],
      icon: <Target className="h-8 w-8 text-purple-600" />,
      color: "purple",
      metrics: {
        leadsIncrease: "400%",
        responseRate: "60%",
        newClients: "150%"
      }
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-600",
        iconBg: "bg-blue-100"
      },
      green: {
        bg: "bg-green-50",
        border: "border-green-200", 
        text: "text-green-600",
        iconBg: "bg-green-100"
      },
      purple: {
        bg: "bg-purple-50",
        border: "border-purple-200",
        text: "text-purple-600", 
        iconBg: "bg-purple-100"
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-green-100 border border-green-200 rounded-full text-sm text-green-600 mb-6">
            <CheckCircle className="w-4 h-4 mr-2" />
            Casos de Éxito Reales
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-gray-800 mb-6">
            Transformaciones{" "}
            <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              exitosas
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Descubre cómo hemos ayudado a empresas de diferentes sectores a 
            automatizar sus procesos y multiplicar sus resultados con IA.
          </p>
        </div>

        {/* Cases Grid */}
        <div className="space-y-12 sm:space-y-16">
          {cases.map((caseItem, index) => {
            const colors = getColorClasses(caseItem.color);
            return (
              <div key={caseItem.id} className="space-y-8">
                {/* Mobile layout */}
                <div className="lg:hidden">
                  <Card className="p-6 sm:p-8 bg-white shadow-lg">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className={`w-12 h-12 sm:w-16 sm:h-16 ${colors.iconBg} rounded-2xl flex items-center justify-center`}>
                        {caseItem.icon}
                      </div>
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{caseItem.title}</h2>
                        <p className="text-base sm:text-lg text-gray-600">{caseItem.subtitle}</p>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-2xl p-4 sm:p-6 mb-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-semibold text-gray-700">Empresa:</span>
                          <p className="text-gray-600">{caseItem.company}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Sector:</span>
                          <p className="text-gray-600">{caseItem.sector}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 flex items-center">
                          <Target className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-red-500" />
                          Desafío
                        </h3>
                        <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{caseItem.challenge}</p>
                      </div>

                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 flex items-center">
                          <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-500" />
                          Solución
                        </h3>
                        <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{caseItem.solution}</p>
                      </div>

                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Funcionalidades implementadas</h3>
                        <div className="grid grid-cols-1 gap-3">
                          {caseItem.features.map((feature, featureIndex) => (
                            <div key={featureIndex} className="flex items-start space-x-3">
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-gray-600">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Results card for mobile */}
                  <Card className={`p-6 sm:p-8 ${colors.bg} ${colors.border} border-2`}>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-6 flex items-center">
                      <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-green-500" />
                      Resultados Obtenidos
                    </h3>
                    
                    <div className="space-y-4 mb-6">
                      {caseItem.results.map((result, resultIndex) => (
                        <div key={resultIndex} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-sm sm:text-base text-gray-700 font-medium">{result}</span>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
                      {Object.entries(caseItem.metrics).map(([key, value], metricIndex) => (
                        <div key={metricIndex} className="text-center">
                          <div className={`text-xl sm:text-2xl font-bold ${colors.text}`}>{value}</div>
                          <div className="text-xs text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>

                {/* Desktop layout */}
                <div className={`hidden lg:flex ${index % 2 === 1 ? 'flex-row-reverse' : 'flex-row'} gap-12 items-center`}>
                  {/* Content */}
                  <div className="flex-1 space-y-6">
                    <div className="flex items-center space-x-4">
                      <div className={`w-16 h-16 ${colors.iconBg} rounded-2xl flex items-center justify-center`}>
                        {caseItem.icon}
                      </div>
                      <div>
                        <h2 className="text-2xl lg:text-3xl font-bold text-gray-800">{caseItem.title}</h2>
                        <p className="text-lg text-gray-600">{caseItem.subtitle}</p>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-2xl p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-semibold text-gray-700">Empresa:</span>
                          <p className="text-gray-600">{caseItem.company}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Sector:</span>
                          <p className="text-gray-600">{caseItem.sector}</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <Target className="w-5 h-5 mr-2 text-red-500" />
                        Desafío
                      </h3>
                      <p className="text-gray-600 leading-relaxed">{caseItem.challenge}</p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <TrendingUp className="w-5 h-5 mr-2 text-blue-500" />
                        Solución
                      </h3>
                      <p className="text-gray-600 leading-relaxed">{caseItem.solution}</p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Funcionalidades implementadas</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {caseItem.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-start space-x-3">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-600">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="flex-1">
                    <Card className={`p-8 ${colors.bg} ${colors.border} border-2`}>
                      <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                        <TrendingUp className="w-6 h-6 mr-2 text-green-500" />
                        Resultados Obtenidos
                      </h3>
                      
                      <div className="space-y-4 mb-6">
                        {caseItem.results.map((result, resultIndex) => (
                          <div key={resultIndex} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700 font-medium">{result}</span>
                          </div>
                        ))}
                      </div>

                      <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
                        {Object.entries(caseItem.metrics).map(([key, value], metricIndex) => (
                          <div key={metricIndex} className="text-center">
                            <div className={`text-2xl font-bold ${colors.text}`}>{value}</div>
                            <div className="text-xs text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 sm:mt-20 text-center">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-white">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">
              ¿Quieres ser nuestro próximo caso de éxito?
            </h2>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto px-4">
              Descubre cómo podemos transformar tu empresa con soluciones de IA 
              personalizadas y medibles.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                onClick={() => window.open('https://cal.com/peter-gal/30min?overlayCalendar=true', '_blank')}
                className="bg-white text-purple-600 hover:bg-gray-100 px-6 sm:px-8 py-3 sm:py-4 font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                <Calendar className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Consulta gratuita
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={() => window.location.href = '/contacto'}
                className="border-white text-white hover:bg-white hover:text-purple-600 px-6 sm:px-8 py-3 sm:py-4 font-semibold text-base sm:text-lg"
              >
                <MessageSquare className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Contactar
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SuccessCases;
